import { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { BackgroundCircles } from './ui/background-circles';

interface PasswordGateProps {
  onPasswordCorrect: () => void;
}

export function PasswordGate({ onPasswordCorrect }: PasswordGateProps) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // The password is set *only* through the environment variable.
  // If VITE_SITE_PASSWORD is not set, the gate is considered disabled.
  const SITE_PASSWORD = import.meta.env.VITE_SITE_PASSWORD || null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // Simple delay to prevent brute force
    setTimeout(() => {
      // Trim whitespace and compare
      const trimmedPassword = password.trim();
      const trimmedSitePassword = SITE_PASSWORD?.trim();
      
      if (trimmedPassword === trimmedSitePassword) {
        // Store in localStorage so they don't need to enter it again
        localStorage.setItem('marketbloom_access', 'granted');
        onPasswordCorrect();
      } else {
        setError('Incorrect password. Please try again.');
        setPassword('');
      }
      setIsLoading(false);
    }, 500);
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Orange circle animated background */}
      <BackgroundCircles variant="orange" />
      
      {/* Content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center px-8 py-8">
        <div className="w-full max-w-6xl mx-auto text-center">
          {/* Hero Section */}
          <div className="mb-12">
            <h1 className="text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold text-slate-900 mb-6 tracking-tight leading-none">
              Sydney's top florists,<br />
              <span className="text-[#F97316]">all in one place.</span>
            </h1>
            
            <p className="text-xl md:text-2xl lg:text-2xl text-slate-600 max-w-3xl mx-auto mb-10 leading-tight font-normal tracking-wide">
              MarketBloom connects independent florists to customers.
            </p>
            
            {/* Support lines */}
            <div className="max-w-3xl mx-auto space-y-3 mb-8">
              <p className="text-lg md:text-xl text-slate-800 font-medium">
                The customer finds you. You fulfil it. That's it.
              </p>
              <p className="text-lg md:text-xl text-slate-800 font-medium">
                Your pricing. Your delivery. Your rules.
              </p>
              <p className="text-lg md:text-xl text-slate-800 font-medium">
                Fewer shops per suburb — quality over quantity.
              </p>
            </div>
            
            <p className="text-base text-slate-600 mb-10">
              You've been invited to preview your store before launch.
            </p>
          </div>

          {/* Password Input Section */}
          <div className="max-w-lg mx-auto">
            <Card className="bg-white/95 backdrop-blur-xl border-0 shadow-2xl">
              <CardContent className="p-10">
                <h2 className="text-2xl font-semibold text-slate-900 mb-8">
                  Enter your password to continue
                </h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Input
                      type="password"
                      placeholder="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full text-lg py-4 px-6 border-2 border-slate-200 focus:border-[#F97316] rounded-lg bg-white/50 backdrop-blur-sm"
                      disabled={isLoading}
                    />
                    {error && (
                      <p className="text-red-500 text-sm mt-3">{error}</p>
                    )}
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full bg-[#967F81] hover:bg-[#843329] text-white text-lg py-4 px-8 rounded-lg font-semibold shadow-lg transition-all duration-200"
                    disabled={isLoading || !password}
                  >
                    {isLoading ? 'Checking...' : 'Unlock Preview'}
                  </Button>
                </form>
                
                <div className="mt-8 pt-6 border-t border-slate-200">
                  <p className="text-slate-600 text-sm">
                    Don't have a password? Email info@marketbloom.com.au
                  </p>
                </div>
                
                {/* Development bypass button */}
                {import.meta.env.DEV && (
                  <div className="mt-6 pt-6 border-t border-slate-200">
                    <Button 
                      onClick={() => {
                        localStorage.setItem('marketbloom_access', 'granted');
                        onPasswordCorrect();
                      }}
                      variant="outline"
                      size="sm"
                      className="w-full"
                    >
                      🚀 Dev Bypass
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}